import { ZoomInOutlined, ZoomOutOutlined } from '@ant-design/icons';
import { DualAxes } from '@ant-design/plots';
import { Card, Empty, Slider, Space } from 'antd';
import { FC, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { CHART_COLORS } from '../../../constants/styles';
import { TrendsIntervalSize } from '../../../types/phlebotomist-dashboard';

// Data point for the product yield trend chart
export interface IProductYieldTrendDataPoint {
  period: string; // e.g., "2024-01-01", "Week 1", "Jan 2024"
  ffpSglExp: number;
  ffpSglAct: number;
  ffpDblExp: number;
  ffpDblAct: number;
  pRbcExp: number;
  pRbcAct: number;
}

// Re-export with shorter alias for backward compatibility
export type ProductYieldTrendDataPoint = IProductYieldTrendDataPoint;

interface IProductYieldTrendChartProps {
  data: ProductYieldTrendDataPoint[];
  loading?: boolean;
  intervalSize: TrendsIntervalSize;
  /** Container width from parent (for debugging/calculations) */
  containerWidth?: number;
  /** Pane width from parent (for debugging/calculations) */
  paneWidth?: number;
}

// Color scheme for each layer (from bottom to top)
// Using brand colors - light colors for "expected" and dark colors for "actual"
const COLORS = {
  ffpAct: CHART_COLORS.FFP.actual, // #0e4d8f Deep navy - FFP Actual
  ffpExp: CHART_COLORS.FFP.expected, // #cedbef Pale blue - FFP Expected
  pRbcAct: CHART_COLORS.pRBC.actual, // #f31a25 Red - pRBC Actual
  pRbcExp: CHART_COLORS.pRBC.expected, // #fecaca Light pink - pRBC Expected
  // Line colors for yield percentages
  ffpPct: CHART_COLORS.lines.ffpPct, // Deep navy - FFP Yield %
  pRbcPct: CHART_COLORS.lines.pRbcPct, // Brand red - pRBC Yield %
  totalPct: CHART_COLORS.lines.totalPct, // Royal blue - Total Yield %
  // Total actual quantity line
  totalAct: CHART_COLORS.lines.totalAct, // Black - Total Actual (thick line)
};

export const ProductYieldTrendChart: FC<IProductYieldTrendChartProps> = ({
  data,
  loading = false,
  intervalSize,
  paneWidth,
}) => {
  const { t } = useTranslation('dashboard.PhlebotomistDashboard');

  // Labels for chart series - Area (units) - Combined FFP
  const ffpActLabel = t('trends.ffpAct', 'FFP Act');
  const ffpExpLabel = t('trends.ffpExp', 'FFP Exp');
  const pRbcActLabel = t('trends.pRbcAct', 'pRBC Act');
  const pRbcExpLabel = t('trends.pRbcExp', 'pRBC Exp');
  // Labels for chart series - Lines (percentages)
  const ffpPctLabel = t('trends.ffpPct', 'FFP %');
  const pRbcPctLabel = t('trends.pRbcPct', 'pRBC %');
  const totalPctLabel = t('trends.totalPct', 'Total Yield %');
  // Label for total actual quantity line
  const totalActLabel = t('trends.totalAct', 'Total Actual');

  // Visibility state for each series
  const [hiddenSeries, setHiddenSeries] = useState<Set<string>>(new Set());

  // ============ ZOOM & SCALING ============
  const CHART_PADDING = 200; // Padding for axes, labels, etc. (px)

  // Target pixels per data point for readable density
  const TARGET_PX_PER_POINT = 15; // ~15px per point is readable without being too sparse

  // Calculate initial zoom level based on data point count
  // Goal: Start at a zoom level where data is readable (not too dense)
  const initialZoom = useMemo(() => {
    const availableWidth = (paneWidth ?? 800) - CHART_PADDING;
    const dataPoints = data.length;

    if (dataPoints === 0) return 4;

    // Calculate what width we'd need at zoom=1 to have TARGET_PX_PER_POINT per data point
    const neededWidth = dataPoints * TARGET_PX_PER_POINT;

    // If the available width is already enough, use zoom 1
    if (neededWidth <= availableWidth) return 1;

    // Calculate the multiplier needed: neededWidth / availableWidth
    const neededMultiplier = neededWidth / availableWidth;

    // Reverse the zoom formula: multiplier = 2^((zoom-1)/3.67)
    // So: zoom = 3.67 * log2(multiplier) + 1
    const calculatedZoom = 3.67 * Math.log2(neededMultiplier) + 1;

    // Clamp to valid range [1, 12] and round to nearest integer
    return Math.min(12, Math.max(1, Math.round(calculatedZoom)));
  }, [data.length, paneWidth]);

  // Zoom state: 1-12 scale, starts at calculated initial zoom
  const [zoom, setZoom] = useState<number | null>(null);
  const effectiveZoom = zoom ?? initialZoom;

  // Reset zoom when data length changes (new date range or interval selected)
  useEffect(() => {
    setZoom(null);
  }, [data.length, intervalSize]);

  // Calculate chart width multiplier based on zoom level
  // Zoom 1 = 1x width (fit all data in available space)
  // Zoom 12 = 8x width (points furthest apart, scrollable)
  const { chartWidth, needsScroll } = useMemo(() => {
    // Exponential interpolation: zoom 1 -> 1x, zoom 12 -> 8x
    // Using formula: 2^((zoom-1)/3.67) which gives ~1 at zoom 1 and ~8 at zoom 12
    const multiplier = Math.pow(2, (effectiveZoom - 1) / 3.67);
    const baseWidth = (paneWidth ?? 800) - CHART_PADDING;
    const calculatedWidth = baseWidth * multiplier;

    return {
      chartWidth: calculatedWidth,
      needsScroll: calculatedWidth > baseWidth,
    };
  }, [effectiveZoom, paneWidth]);

  const toggleSeries = (label: string) => {
    setHiddenSeries((prev) => {
      const next = new Set(prev);
      if (next.has(label)) {
        next.delete(label);
      } else {
        next.add(label);
      }
      return next;
    });
  };

  // Transform data for stacked area chart - combining FFP SGL + FFP DBL into FFP
  // Stack order from bottom: FFP Act, FFP Gap, pRBC Act, pRBC Gap
  const areaData = useMemo(() => {
    const items: { period: string; value: number; type: string }[] = [];

    data.forEach((d) => {
      // Combined FFP values
      const ffpAct = d.ffpSglAct + d.ffpDblAct;
      const ffpExp = d.ffpSglExp + d.ffpDblExp;

      // FFP Actual (bottom layer)
      const ffpActHidden = hiddenSeries.has(ffpActLabel);
      if (!ffpActHidden) {
        items.push({
          period: d.period,
          value: ffpAct,
          type: ffpActLabel,
        });
      }
      // FFP Exp: show full expected if Act is hidden, otherwise show gap
      if (!hiddenSeries.has(ffpExpLabel)) {
        items.push({
          period: d.period,
          value: ffpActHidden ? ffpExp : Math.max(0, ffpExp - ffpAct),
          type: ffpExpLabel,
        });
      }
      // pRBC Actual
      const pRbcActHidden = hiddenSeries.has(pRbcActLabel);
      if (!pRbcActHidden) {
        items.push({
          period: d.period,
          value: d.pRbcAct,
          type: pRbcActLabel,
        });
      }
      // pRBC Exp: show full expected if Act is hidden, otherwise show gap
      if (!hiddenSeries.has(pRbcExpLabel)) {
        items.push({
          period: d.period,
          value: pRbcActHidden ? d.pRbcExp : Math.max(0, d.pRbcExp - d.pRbcAct),
          type: pRbcExpLabel,
        });
      }
    });

    return items;
  }, [
    data,
    hiddenSeries,
    ffpActLabel,
    ffpExpLabel,
    pRbcActLabel,
    pRbcExpLabel,
  ]);

  // Calculate the max stacked value for Y-axis domain
  // Also consider totalActData when area series are hidden
  const maxStackedValue = useMemo(() => {
    let maxVal = 0;
    data.forEach((d) => {
      // Combined FFP values
      const ffpAct = d.ffpSglAct + d.ffpDblAct;
      const ffpExp = d.ffpSglExp + d.ffpDblExp;

      // Calculate total stack height for this period (considering hidden series)
      let stackHeight = 0;
      if (!hiddenSeries.has(ffpActLabel)) stackHeight += ffpAct;
      if (!hiddenSeries.has(ffpExpLabel)) {
        stackHeight += hiddenSeries.has(ffpActLabel)
          ? ffpExp
          : Math.max(0, ffpExp - ffpAct);
      }
      if (!hiddenSeries.has(pRbcActLabel)) stackHeight += d.pRbcAct;
      if (!hiddenSeries.has(pRbcExpLabel)) {
        stackHeight += hiddenSeries.has(pRbcActLabel)
          ? d.pRbcExp
          : Math.max(0, d.pRbcExp - d.pRbcAct);
      }
      maxVal = Math.max(maxVal, stackHeight);

      // Also consider Total Actual line value when all area series are hidden
      if (!hiddenSeries.has(totalActLabel)) {
        const totalAct = d.ffpSglAct + d.ffpDblAct + d.pRbcAct;
        maxVal = Math.max(maxVal, totalAct);
      }
    });
    // Add 10% padding to the top, ensure minimum of 1 to avoid 0 domain
    return Math.max(1, Math.ceil(maxVal * 1.1));
  }, [
    data,
    hiddenSeries,
    ffpActLabel,
    ffpExpLabel,
    pRbcActLabel,
    pRbcExpLabel,
    totalActLabel,
  ]);

  // Performance: disable animations for large datasets
  const isLargeDataset = data.length > 50;

  // Always use smooth curves for lines
  const lineShape = 'smooth';

  // Line data for yield percentages (Actual / Expected * 100) - Combined FFP
  const ffpPctData = useMemo(() => {
    if (hiddenSeries.has(ffpPctLabel)) return [];
    return data.map((d) => {
      const ffpAct = d.ffpSglAct + d.ffpDblAct;
      const ffpExp = d.ffpSglExp + d.ffpDblExp;
      return {
        period: d.period,
        value: ffpExp > 0 ? Math.round((ffpAct / ffpExp) * 100) : 0,
        type: ffpPctLabel,
      };
    });
  }, [data, hiddenSeries, ffpPctLabel]);

  const pRbcPctData = useMemo(() => {
    if (hiddenSeries.has(pRbcPctLabel)) return [];
    return data.map((d) => ({
      period: d.period,
      value: d.pRbcExp > 0 ? Math.round((d.pRbcAct / d.pRbcExp) * 100) : 0,
      type: pRbcPctLabel,
    }));
  }, [data, hiddenSeries, pRbcPctLabel]);

  const totalPctData = useMemo(() => {
    if (hiddenSeries.has(totalPctLabel)) return [];
    return data.map((d) => {
      const totalExp = d.ffpSglExp + d.ffpDblExp + d.pRbcExp;
      const totalAct = d.ffpSglAct + d.ffpDblAct + d.pRbcAct;
      return {
        period: d.period,
        value: totalExp > 0 ? Math.round((totalAct / totalExp) * 100) : 0,
        type: totalPctLabel,
      };
    });
  }, [data, hiddenSeries, totalPctLabel]);

  // Total actual quantity line data (uses left Y-axis with product units)
  const totalActData = useMemo(() => {
    if (hiddenSeries.has(totalActLabel)) return [];
    return data.map((d) => ({
      period: d.period,
      value: d.ffpSglAct + d.ffpDblAct + d.pRbcAct,
      type: totalActLabel,
    }));
  }, [data, hiddenSeries, totalActLabel]);

  // Combine all percentage line data
  const combinedLineData = useMemo(() => {
    return [...ffpPctData, ...pRbcPctData, ...totalPctData];
  }, [ffpPctData, pRbcPctData, totalPctData]);

  // Single DualAxes config combining stacked area and lines
  const config = useMemo(
    () => ({
      xField: 'period',
      legend: false,
      animate: !isLargeDataset,
      axis: {
        x: {
          label: {
            style: {
              fontWeight: (
                _datum: { index: number },
                index: number
              ): number | string => (index % 2 === 0 ? 'bold' : 'normal'),
            },
          },
        },
      },
      children: [
        // Stacked area chart for product units (left Y-axis)
        {
          data: areaData,
          type: 'area',
          yField: 'value',
          colorField: 'type',
          stack: true,
          shapeField: 'smooth',
          animate: !isLargeDataset,
          scale: {
            y: { key: 'units', domain: [0, maxStackedValue] },
          },
          style: {
            fillOpacity: 0.85,
          },
          axis: {
            y: {
              position: 'left',
              title: t('trends.productUnits', 'Product Units'),
            },
          },
          interaction: isLargeDataset
            ? { elementHighlight: false }
            : { elementHighlight: { background: true } },
        },
        // Line chart for Total Actual (uses left Y-axis with product units)
        ...(totalActData.length > 0
          ? [
              {
                data: totalActData,
                type: 'line',
                yField: 'value',
                colorField: 'type',
                shapeField: lineShape,
                animate: !isLargeDataset,
                scale: {
                  y: { key: 'units', domain: [0, maxStackedValue] },
                },
                style: {
                  stroke: COLORS.totalAct,
                  lineWidth: 4,
                },
                axis: { y: false },
              },
            ]
          : []),
        // Line chart for FFP % (uses right Y-axis)
        ...(ffpPctData.length > 0
          ? [
              {
                data: ffpPctData,
                type: 'line',
                yField: 'value',
                colorField: 'type',
                shapeField: lineShape,
                animate: !isLargeDataset,
                scale: {
                  y: { key: 'pct', domain: [0, 120] },
                },
                style: {
                  stroke: COLORS.ffpPct,
                  lineWidth: 2,
                },
                axis: { y: false },
              },
            ]
          : []),
        // Line chart for pRBC % (uses right Y-axis)
        ...(pRbcPctData.length > 0
          ? [
              {
                data: pRbcPctData,
                type: 'line',
                yField: 'value',
                colorField: 'type',
                shapeField: lineShape,
                animate: !isLargeDataset,
                scale: {
                  y: { key: 'pct', domain: [0, 120] },
                },
                style: {
                  stroke: COLORS.pRbcPct,
                  lineWidth: 2,
                },
                axis: { y: false },
              },
            ]
          : []),
        // Line chart for Total Yield % (uses right Y-axis - this one shows the axis)
        // Also defines the GLOBAL color scale for all series in the chart
        ...(totalPctData.length > 0
          ? [
              {
                data: totalPctData,
                type: 'line',
                yField: 'value',
                colorField: 'type',
                shapeField: lineShape,
                animate: !isLargeDataset,
                scale: {
                  y: { key: 'pct', domain: [0, 120] },
                  color: {
                    // GLOBAL color scale - must include ALL series types
                    // Order: area types first, then line types
                    domain: [
                      ffpActLabel,
                      ffpExpLabel,
                      pRbcActLabel,
                      pRbcExpLabel,
                      totalActLabel,
                      ffpPctLabel,
                      pRbcPctLabel,
                      totalPctLabel,
                    ],
                    range: [
                      COLORS.ffpAct,
                      COLORS.ffpExp,
                      COLORS.pRbcAct,
                      COLORS.pRbcExp,
                      COLORS.totalAct,
                      COLORS.ffpPct,
                      COLORS.pRbcPct,
                      COLORS.totalPct,
                    ],
                  },
                },
                style: {
                  stroke: COLORS.totalPct,
                  lineWidth: 2,
                },
                axis: {
                  y: {
                    position: 'right',
                    title: t('trends.yieldPct', 'Yield (%)'),
                  },
                },
              },
            ]
          : // If no totalPctData but other pct lines exist, show axis on the last one
          combinedLineData.length > 0
          ? [
              {
                data: [],
                type: 'line',
                yField: 'value',
                scale: {
                  y: { key: 'pct', domain: [0, 120] },
                  color: {
                    // GLOBAL color scale - must include ALL series types
                    domain: [
                      ffpActLabel,
                      ffpExpLabel,
                      pRbcActLabel,
                      pRbcExpLabel,
                      totalActLabel,
                      ffpPctLabel,
                      pRbcPctLabel,
                      totalPctLabel,
                    ],
                    range: [
                      COLORS.ffpAct,
                      COLORS.ffpExp,
                      COLORS.pRbcAct,
                      COLORS.pRbcExp,
                      COLORS.totalAct,
                      COLORS.ffpPct,
                      COLORS.pRbcPct,
                      COLORS.totalPct,
                    ],
                  },
                },
                axis: {
                  y: {
                    position: 'right',
                    title: t('trends.yieldPct', 'Yield (%)'),
                  },
                },
              },
            ]
          : []),
      ],
    }),
    [
      areaData,
      ffpActLabel,
      ffpExpLabel,
      pRbcActLabel,
      pRbcExpLabel,
      ffpPctData,
      pRbcPctData,
      totalPctData,
      totalActData,
      totalActLabel,
      ffpPctLabel,
      pRbcPctLabel,
      totalPctLabel,
      combinedLineData.length,
      isLargeDataset,
      lineShape,
      maxStackedValue,
      t,
    ]
  );

  // Custom legend items - grouped by product type, then lines
  const legendItems = [
    { label: ffpActLabel, color: COLORS.ffpAct },
    { label: ffpExpLabel, color: COLORS.ffpExp },
    { label: pRbcActLabel, color: COLORS.pRbcAct },
    { label: pRbcExpLabel, color: COLORS.pRbcExp },
    // Total actual line (thick black)
    {
      label: totalActLabel,
      color: COLORS.totalAct,
      isLine: true,
      isThick: true,
    },
    // Percentage lines
    { label: ffpPctLabel, color: COLORS.ffpPct, isLine: true },
    { label: pRbcPctLabel, color: COLORS.pRbcPct, isLine: true },
    { label: totalPctLabel, color: COLORS.totalPct, isLine: true },
  ];

  const getIntervalLabel = () => {
    const labels: Record<TrendsIntervalSize, string> = {
      day: t('intervalSize.day', 'Day'),
      week: t('intervalSize.week', 'Week'),
      month: t('intervalSize.month', 'Month'),
      quarter: t('intervalSize.quarter', 'Quarter'),
      year: t('intervalSize.year', 'Year'),
    };
    return labels[intervalSize];
  };

  if (data.length === 0) {
    return (
      <Card
        title={t(
          'trends.productYieldTitle',
          'Product Expected vs Actual - Trends'
        )}
        loading={loading}
      >
        <Empty description={t('trends.noData', 'No data available')} />
      </Card>
    );
  }

  return (
    <Card
      title={`${t(
        'trends.productYieldTitle',
        'Product Expected vs Actual - Trends'
      )} (${t('trends.by', 'by')} ${getIntervalLabel()})`}
      loading={loading}
      style={{ position: 'relative' }}
    >
      {/* Custom Legend - clickable to toggle visibility */}
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 8,
          marginBottom: 16,
          justifyContent: 'center',
        }}
      >
        {legendItems.map((item) => {
          const isHidden = hiddenSeries.has(item.label);
          const isLine = 'isLine' in item && item.isLine;
          const isThick = 'isThick' in item && item.isThick;
          return (
            <button
              key={item.label}
              type="button"
              onClick={() => toggleSeries(item.label)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                padding: '4px 8px',
                border: '1px solid #d9d9d9',
                borderRadius: 4,
                background: isHidden ? '#f5f5f5' : '#fff',
                cursor: 'pointer',
                opacity: isHidden ? 0.5 : 1,
                transition: 'all 0.2s',
              }}
            >
              {isLine ? (
                // Line icon for line series (thick or thin)
                <span
                  style={{
                    display: 'inline-block',
                    width: 16,
                    height: isThick ? 5 : 3,
                    backgroundColor: isHidden ? '#d9d9d9' : item.color,
                    borderRadius: 1,
                    transition: 'background-color 0.2s',
                  }}
                />
              ) : (
                // Square icon for area series
                <span
                  style={{
                    display: 'inline-block',
                    width: 12,
                    height: 12,
                    backgroundColor: isHidden ? '#d9d9d9' : item.color,
                    borderRadius: 2,
                    transition: 'background-color 0.2s',
                  }}
                />
              )}
              <span
                style={{
                  fontSize: 12,
                  textDecoration: isHidden ? 'line-through' : 'none',
                  color: isHidden ? '#999' : '#333',
                }}
              >
                {item.label}
              </span>
            </button>
          );
        })}
      </div>

      {/* Zoom control */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-end',
          marginBottom: 8,
          gap: 8,
        }}
      >
        <span style={{ fontSize: 12, color: '#999' }}>
          {t('trends.zoomLabel', 'Zoom')}:
        </span>
        <Space size="small">
          <ZoomOutOutlined style={{ color: '#999' }} />
          <Slider
            min={1}
            max={12}
            step={1}
            value={effectiveZoom}
            onChange={setZoom}
            style={{ width: 150 }}
          />
          <ZoomInOutlined style={{ color: '#999' }} />
        </Space>
      </div>

      {/* Scrollable chart container */}
      <div
        style={{
          overflowX: needsScroll ? 'auto' : 'hidden',
          overflowY: 'hidden',
        }}
      >
        <div
          style={{
            width: chartWidth,
            minWidth: chartWidth,
          }}
        >
          <DualAxes {...config} height={650} width={chartWidth} />
        </div>
      </div>
    </Card>
  );
};

export default ProductYieldTrendChart;
